import { Icon } from '@iconify/react';
import { LoaderCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../Shared/Accordion/Accordion';
import { Button } from '../../Shared/Button/Button';
import { Input } from '../../Shared/Input/Input';
import { ApiDataTable, HttpItemCategory } from '../ApiDataTable/ApiDataTable';
import { RemoteMcpDto } from '@/Domain/api/Action/dto/RemoteMCP.dto';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/Domain/components/Shared/Form/Form';
import { useConnectRemoteMcpActionForm } from '@/Domain/hooks/Action/RemoteMcp/useConnectRemoteMcpActionForm.hook';

type Props = {
  remoteMcpAction?: RemoteMcpDto;
};

const PlainConnectRemoteMcpForm = ({ remoteMcpAction }: Props) => {
  const { t } = useTranslation('remote_mcp');
  const { form, handleSubmit, isPending } = useConnectRemoteMcpActionForm(remoteMcpAction);

  return (
    <Form {...form}>
      <FormField
        name="remote_mcp_url"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('form.remote_mcp_url_label')}</FormLabel>
            <FormControl>
              <Input
                data-testid="remote-mcp-action-url"
                placeholder={t('form.remote_mcp_url_placeholder')}
                {...field}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormLabel>{t('form.remote_mcp_connection_headers')}</FormLabel>
      <ApiDataTable name={HttpItemCategory.HEADERS} form={form} withInsertBadge={false} />
      <div className="mt-4 flex justify-center">
        {isPending ? (
          <LoaderCircle className="animate-spin" />
        ) : (
          <Button
            onClick={form.handleSubmit(handleSubmit)}
            data-testid="remote-mcp-action-sheet-connect-button"
            hidden={isPending}
          >
            {remoteMcpAction ? t('button.reconnect') : t('button.connect')}
          </Button>
        )}
      </div>
    </Form>
  );
};

type ConnectRemoteMcpProps = {
  remoteMcpAction?: RemoteMcpDto;
  withAccordion: boolean;
};

export const ConnectRemoteMcpForm = ({ remoteMcpAction, withAccordion }: ConnectRemoteMcpProps) => {
  const { t } = useTranslation('remote_mcp');
  if (!withAccordion) {
    return <PlainConnectRemoteMcpForm remoteMcpAction={remoteMcpAction} />;
  }

  return (
    <Accordion
      type="single"
      collapsible
      className="w-full"
      data-testid="knowledge-base-action-sheet-advanced-options-accordion"
    >
      <AccordionItem
        value="connection_details"
        className="rounded-xl border border-border py-2 px-6"
      >
        <AccordionTrigger className="text-foreground font-medium px-1">
          <div className="flex items-center gap-3">
            <Icon icon="mingcute:settings-2-line" /> {t('form.connection_details')}
          </div>
        </AccordionTrigger>
        <AccordionContent className="border-t pt-4 flex flex-col gap-10 px-1">
          <PlainConnectRemoteMcpForm remoteMcpAction={remoteMcpAction} />
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};
